import Card from "components/card";
import { MdOutlineSchool, MdOutlineReceipt, MdPayment, MdCheckCircle, MdPending, MdError, MdDownload, MdVisibility } from "react-icons/md";

// Helper function to format the date
const formatDate = (date) => {
  const options = { day: "2-digit", month: "short", year: "numeric" };
  return new Date(date).toLocaleDateString("en-GB", options);
};

// Helper function to format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-KE', {
    style: 'currency',
    currency: 'KES',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

// Helper function to get status styling
const getStatusStyle = (status) => {
  switch (status?.toUpperCase()) {
    case 'PAID':
      return {
        bgColor: 'bg-green-100 dark:bg-green-900/30',
        textColor: 'text-green-700 dark:text-green-400',
        borderColor: 'border-green-200 dark:border-green-700',
        icon: <MdCheckCircle className="text-green-500" />,
        dotColor: 'bg-green-500'
      };
    case 'PENDING':
      return {
        bgColor: 'bg-yellow-100 dark:bg-yellow-900/30',
        textColor: 'text-yellow-700 dark:text-yellow-400',
        borderColor: 'border-yellow-200 dark:border-yellow-700',
        icon: <MdPending className="text-yellow-500" />,
        dotColor: 'bg-yellow-500'
      };
    case 'FAILED':
      return {
        bgColor: 'bg-red-100 dark:bg-red-900/30',
        textColor: 'text-red-700 dark:text-red-400',
        borderColor: 'border-red-200 dark:border-red-700',
        icon: <MdError className="text-red-500" />,
        dotColor: 'bg-red-500'
      };
    default:
      return {
        bgColor: 'bg-gray-100 dark:bg-gray-900/30',
        textColor: 'text-gray-700 dark:text-gray-400',
        borderColor: 'border-gray-200 dark:border-gray-700',
        icon: <MdPending className="text-gray-500" />,
        dotColor: 'bg-gray-500'
      };
  }
};

// Helper function to get payment method icon
const getPaymentMethodIcon = (method) => {
  switch (method?.toLowerCase()) {
    case 'mpesa':
      return '📱';
    case 'waafipay':
      return '💳';
    case 'card':
      return '💳';
    default:
      return '💳';
  }
};

const YourTransaction = ({ transactions }) => {
  return (
    <Card extra="w-full h-full p-4 flex flex-col justify-between">
      <div className="flex items-center justify-between mb-6">
        <h4 className="text-lg font-bold text-navy-700 dark:text-white flex items-center gap-2">
          <MdOutlineReceipt className="text-brand-500" />
          Your Transactions
        </h4>
        <div className="text-sm text-gray-500 dark:text-gray-400">
          {transactions?.length || 0} total
        </div>
      </div>

      <div className="flex-grow">
        {!transactions || transactions.length === 0 ? (
          // Centered message when there are no transactions
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="w-16 h-16 bg-gray-100 dark:bg-navy-800 rounded-full flex items-center justify-center mb-4">
              <MdOutlineReceipt className="text-2xl text-gray-400" />
            </div>
            <p className="text-gray-500 dark:text-gray-400 mb-2 font-medium">
              No transactions found
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500">
              Your transaction history will appear here once you make a purchase
            </p>
          </div>
        ) : (
          // List of transactions
          <div className="space-y-3">
            {transactions.slice(0, 5).map((transaction) => {
              const statusStyle = getStatusStyle(transaction.status);
              const paymentIcon = getPaymentMethodIcon(transaction.paymentMethod);

              return (
                <div 
                  key={transaction.id} 
                  className="p-4 bg-gray-50 dark:bg-navy-800 rounded-lg border border-gray-200 dark:border-navy-700 hover:shadow-md transition-all duration-200"
                >
                  {/* Transaction Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-brand-100 dark:bg-brand-900/30 rounded-lg flex items-center justify-center">
                        <MdOutlineSchool className="text-brand-500 text-lg" />
                      </div>
                      <div className="flex-1">
                        <h5 className="text-sm font-semibold text-navy-700 dark:text-white mb-1">
                          {transaction.courseTitle || "Course Purchase"}
                        </h5>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {formatDate(transaction.paymentDate)}
                        </p>
                      </div>
                    </div>
                    
                    {/* Amount */}
                    <div className="text-right">
                      <div className="text-sm font-bold text-navy-700 dark:text-white">
                        {formatCurrency(transaction.totalPaid)}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {paymentIcon} {transaction.paymentMethod || 'M-Pesa'}
                      </div>
                    </div>
                  </div>

                  {/* Transaction Details */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {/* Status Badge */}
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${statusStyle.bgColor} ${statusStyle.textColor} ${statusStyle.borderColor}`}>
                        {statusStyle.icon}
                        {transaction.status}
                      </span>
                      
                      {/* Reference */}
                      {transaction.transactionReference && (
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          Ref: {transaction.transactionReference.slice(-8)}
                        </span>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center gap-1">
                      <button className="p-1.5 text-gray-400 hover:text-brand-500 transition-colors" title="View Details">
                        <MdVisibility className="text-sm" />
                      </button>
                      {transaction.status === 'PAID' && (
                        <button className="p-1.5 text-gray-400 hover:text-green-500 transition-colors" title="Download Receipt">
                          <MdDownload className="text-sm" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="mt-6 pt-4 border-t border-gray-200 dark:border-navy-700">
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Showing {Math.min(transactions?.length || 0, 5)} of {transactions?.length || 0} transactions
          </div>
          <button className="text-sm font-medium text-brand-500 hover:text-brand-600 transition-colors flex items-center gap-1">
            View All Transactions
            <MdVisibility className="text-xs" />
          </button>
        </div>
      </div>
    </Card>
  );
};

export default YourTransaction;
